import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import ListFilterSelectChipTrigger, {
  ListFilterTriggerValue,
} from '@common/ui/ListFilters/ListFilterSelectChipTrigger';
import AqlBuilderSelect from '@Inventory/AqlBuilder/components/AqlBuilderSelect';
import { ListFilterOption } from '@common/ui/ListFilters/common';
import {
  AqlBuilderOperator,
  AqlBuilderOperatorShortDisplayName,
  isFreeTextOperator,
  AqlFieldExpression,
  AqlBuilderField,
  isFieldHasKey,
  useGetQueryQP,
  isIdField,
} from '@Inventory/AqlBuilder/helpers/common';
import { useInventoryFilterOptions } from '@Inventory/services/use-inventory-filter-options';
import { getFragmentData } from '@api/gql';
import { INVENTORY_FILTER_OPTIONS_FRAGMENT } from '@Inventory/services/use-inventory-filter-options.gql';
import ListFilterSingleSelect from '@common/ui/ListFilters/ListFilterSingleSelect';
import { MaterialIcon } from '@components';
import ListFilterSelectTitle from '@common/ui/ListFilters/ListFilterSelectTitle';
import { useInventoryTriggerValues } from '@Inventory/services/use-inventory-trigger-values';
import ListFilterPopover from '@common/components/ListFilterPopover';

// yup, its hardcoded to false coz backend doesnt support it yet
// but i want to keep it here for future reference
const USE_QUERY_AS_SEARCH_IN_FILTER_OPTIONS = false;

interface AqlBuilderFilterProps {
  field: AqlBuilderField;
  operators: AqlBuilderOperator[];
  expression?: AqlFieldExpression;
  onExpressionChange: (newValue?: AqlFieldExpression) => void;
  label: string;
  opened?: boolean;
  active?: boolean;
  setOpened?: (opened: boolean) => void;
  onClear?: () => void;
}

export default function AqlBuilderFilter({
  field,
  operators,
  expression,
  onExpressionChange,
  label,
  opened = false,
  active = false,
  setOpened,
  onClear,
}: AqlBuilderFilterProps) {
  const initOperator = operators[0] || AqlBuilderOperator['='];

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | undefined>(undefined);

  const [fieldKey, setFieldKey] = useState<string | undefined>(expression?.key);
  const [operator, setOperator] = useState(() => expression?.operator || initOperator);
  const value = useMemo(() => expression?.value || [], [expression]);
  const [valuesState] = useState(expression?.value);

  const [search, setSearch] = useState('');

  const keyword = useMemo(() => {
    if (isFieldHasKey(field) && fieldKey) {
      return `${field}["${fieldKey}"]`;
    }

    return field;
  }, [field, fieldKey]);

  const baseQuery = useGetQueryQP([field]);
  const { query, searchQuery } = useMemo(() => {
    if (!USE_QUERY_AS_SEARCH_IN_FILTER_OPTIONS) {
      return { query: '', searchQuery: search };
    }

    let queryString = baseQuery;
    let searchQueryString = '';

    if (!search) {
      return { query: baseQuery || '', searchQuery: searchQueryString };
    }

    if (isIdField(field)) {
      searchQueryString = search;
    } else if (isFieldHasKey(field) && fieldKey) {
      queryString = `${field}["${fieldKey}"] contains "${search}" ${queryString ? `and (${queryString})` : ''}`;
    } else {
      queryString = `${field} contains "${search}" ${queryString ? `and (${queryString})` : ''}`;
    }

    return {
      query: queryString,
      searchQuery: searchQueryString,
    };
  }, [baseQuery, field, fieldKey, search]);

  const { isFetched, data, hasNextPage, fetchNextPage, isFetchingNextPage } = useInventoryFilterOptions(
    {
      keyword,
      query,
      searchQuery,
      selected: valuesState,
    },
    opened || active,
  );

  const options: ListFilterOption[] = useMemo(() => {
    const list =
      data?.pages.flatMap((page) =>
        getFragmentData(INVENTORY_FILTER_OPTIONS_FRAGMENT, page.inventoryFilterOptions.data),
      ) ?? [];

    return list.map((o) => ({
      value: o.key,
      label: o.label,
      icon: o.icon,
      endIcon: isFieldHasKey(field) && !fieldKey && <MaterialIcon symbol="chevron_right" size="small" />,
    }));
  }, [data?.pages, field, fieldKey]);

  const selectedOptions = useMemo(() => {
    if (isFreeTextOperator(operator)) {
      return value.map((v) => ({ value: v, label: v }));
    }

    return options.filter((o) => value.includes(o.value));
  }, [operator, options, value]);

  const selectedKey = useMemo(() => {
    return options.find((o) => o.value === expression?.key);
  }, [expression?.key, options]);

  const handleFieldValueChange = useCallback(
    (newOperator: AqlBuilderOperator = operator, newOptions: ListFilterOption[] = selectedOptions) => {
      setOperator(newOperator);
      const newValuesList = newOptions.map((o) => o.value);
      onExpressionChange({
        key: fieldKey,
        operator: newOperator,
        value: newValuesList,
      });
    },
    [operator, selectedOptions, onExpressionChange, fieldKey],
  );

  const handleClear = useCallback(() => {
    if (onClear) {
      onClear();
    } else {
      handleFieldValueChange(initOperator, []);
    }
  }, [handleFieldValueChange, initOperator, onClear]);

  const handleOpen = useCallback(
    (element: HTMLButtonElement) => {
      setAnchorEl(element);
      setSearch(isFreeTextOperator(operator) && selectedOptions.length > 0 ? (selectedOptions[0]?.label ?? '') : '');
      setOpened && setOpened(true);
      setFieldKey(expression?.key);
    },
    [operator, selectedOptions, setOpened, expression?.key],
  );

  const handleClose = useCallback(() => {
    setAnchorEl(undefined);
    setOpened && setOpened(false);
  }, [setOpened]);

  const handleSearch = useCallback(
    (newSearchVal: string, newOperator: AqlBuilderOperator = operator) => {
      setSearch(newSearchVal);
      if (isFreeTextOperator(newOperator)) {
        handleFieldValueChange(newOperator, [{ value: newSearchVal, label: newSearchVal }]);
      }
    },
    [operator, handleFieldValueChange],
  );

  const handleOperatorChange = useCallback(
    (newOperator: AqlBuilderOperator) => {
      if (isFreeTextOperator(newOperator) && selectedOptions.length > 0) {
        handleSearch(selectedOptions[0].label, newOperator);
      } else {
        handleFieldValueChange(newOperator, isFreeTextOperator(operator) ? [] : selectedOptions);
      }
    },
    [selectedOptions, operator, handleSearch, handleFieldValueChange],
  );

  const handleSelectValues = useCallback(
    (values: ListFilterOption[]) => {
      if (isFreeTextOperator(operator) && values.length > 0) {
        handleSearch(values[0].label);
        handleClose();
      } else {
        handleFieldValueChange(operator, values);
      }
    },
    [operator, handleSearch, handleClose, handleFieldValueChange],
  );

  const handleSelectKey = useCallback((key?: ListFilterOption) => {
    setFieldKey(key?.value);
    setSearch('');
  }, []);

  const ref = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (ref.current) {
      setAnchorEl(ref.current);
    }
  }, [opened, ref]);

  const aqlListTitle = useMemo(() => {
    if (isFieldHasKey(field) && fieldKey) {
      return (
        <ListFilterSelectTitle
          title={`Select values of ${fieldKey}`}
          onAction={() => {
            handleSelectKey(undefined);
          }}
          actionText="Back"
        />
      );
    }

    return <ListFilterSelectTitle title={`Select ${label}`} />;
  }, [field, fieldKey, handleSelectKey, label]);

  const triggerLabel = useMemo(() => {
    if (isFieldHasKey(field) && expression?.key) {
      return `${label}: ${expression?.key}`;
    }

    return label;
  }, [field, expression?.key, label]);

  const handleBottomReached = useCallback(() => {
    if (hasNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage]);

  const { triggerValuesFromOptions, triggerValuesToPreload } = useMemo(() => {
    const valuesFromOptions: Map<string, string> = new Map();
    const valuesToPreload: string[] = [];
    for (const v of value) {
      const option = options.find((o) => o.value === v);
      if (option) {
        valuesFromOptions.set(option.value, option.label);
      } else {
        valuesToPreload.push(v);
      }
    }

    return { triggerValuesFromOptions: valuesFromOptions, triggerValuesToPreload: valuesToPreload };
  }, [options, value]);

  const { data: triggerValuesPreloaded, isFetched: isInventoryTriggerValuesFetched } = useInventoryTriggerValues(
    triggerValuesToPreload,
    field,
    triggerValuesToPreload.length > 0,
  );

  const triggerValues = useMemo(() => {
    const values: ListFilterTriggerValue[] = [];
    for (const v of value) {
      let val: string | undefined = triggerValuesFromOptions.get(v);
      if (!val) {
        val = triggerValuesPreloaded?.get(v);
      }

      if (!val) {
        val = v;
      }

      values.push({ label: val });
    }
    return values;
  }, [triggerValuesFromOptions, triggerValuesPreloaded, value]);

  const triggerLoading = useMemo(() => {
    if (triggerValuesToPreload.length === 0) {
      return false;
    }

    return !isInventoryTriggerValuesFetched;
  }, [isInventoryTriggerValuesFetched, triggerValuesToPreload.length]);

  return (
    <>
      <ListFilterSelectChipTrigger
        ref={ref}
        active={opened}
        label={triggerLabel}
        operator={AqlBuilderOperatorShortDisplayName[operator]}
        value={!triggerLoading ? triggerValues : []}
        onClear={handleClear}
        onClick={(e) => handleOpen(e.currentTarget)}
        loading={triggerLoading}
      />
      <ListFilterPopover open={!!anchorEl && opened} anchorEl={anchorEl} onClose={handleClose}>
        {isFieldHasKey(field) && !fieldKey ? (
          <ListFilterSingleSelect
            width={300}
            options={options}
            value={selectedKey}
            onSelect={handleSelectKey}
            searchValue={search}
            onSearch={handleSearch}
            title={`Select ${label}`}
            onBottomReached={handleBottomReached}
            isFetching={!isFetched}
          />
        ) : (
          <AqlBuilderSelect
            width={300}
            title={aqlListTitle}
            operatorOptions={operators}
            selectedOperator={operator}
            onSelectOperator={handleOperatorChange}
            valuesOptions={options}
            selectedValues={selectedOptions}
            onSelectValues={handleSelectValues}
            searchValue={search}
            onSearchValue={handleSearch}
            isFetching={!isFetched}
            isLoading={!isFetched || isFetchingNextPage}
            onBottomReached={handleBottomReached}
          />
        )}
      </ListFilterPopover>
    </>
  );
}
